"use client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { API_ROUTES } from "utils/api-route.constants";
import {
  LGSMatchedCardsRequest,
  LGSMatchedCardsResponse,
} from "modules/lgs/model/lgs";
import { ItemSortOptionsEnum } from "modules/tradeLists/model/tradeList";
import { SortOrderEnum } from "modules/collections/details/model/collection";
import { useEffect, useRef } from "react";
import {
  INFINITE_ALWAYS_FRESH_CONFIG,
  keepPreviousDataScopedTo,
} from "modules/common/react-query-configs";
import { useAuth } from "@clerk/nextjs";

export const useInfiniteLGSMatchedCards = (
  req: LGSMatchedCardsRequest & {
    query?: string;
    sort?: ItemSortOptionsEnum;
    order?: SortOrderEnum;
  },
  options?: { enabled?: boolean }
) => {
  const { isLoaded, isSignedIn } = useAuth();
  const fetch = useFetchWrapper();
  const loadMoreRef = useRef(null);

  const {
    data,
    isSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isPending,
    isFetching,
  } = useInfiniteQuery<LGSMatchedCardsResponse>({
    queryKey: [QUERIES.LGS_MATCHED_CARDS, req],
    enabled: (options?.enabled ?? true) && isLoaded && isSignedIn,
    queryFn: ({ pageParam }) =>
      getMatchedCards({
        ...req,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    ...INFINITE_ALWAYS_FRESH_CONFIG, // Always-fresh: drain-debounced reads, no caching
    // Hold the previous rows while a re-keyed search/sort/facet fetches, but
    // only within the same store — see react-query-configs.ts.
    placeholderData: keepPreviousDataScopedTo<LGSMatchedCardsRequest>(
      (previousReq) => previousReq?.wotcId === req.wotcId
    ),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.length + 1;
      } else {
        return undefined;
      }
    },
  });

  useEffect(() => {
    if (!hasNextPage || !loadMoreRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    isLoading,
    isSuccess,
    isFetching,
    data,
    loadMoreRef,
    isFetchingNextPage,
    isPending,
    totalResults: data?.pages[0].total,
    currentCount:
      data?.pages.reduce((acc, page) => acc + page.items.length, 0) ?? 0,
    hasNextPage,
    fetchNextPage,
  };

  async function getMatchedCards(
    reg: LGSMatchedCardsRequest & {
      query?: string;
      sort?: ItemSortOptionsEnum;
      order?: SortOrderEnum;
    }
  ) {
    // Build query parameters
    const params = new URLSearchParams({
      page: reg.page.toString(),
      per_page: reg.perPage.toString(),
      store_id: req.wotcId.toString(),
    });

    // Add search query if provided
    if (reg.query && reg.query.trim() !== "") {
      params.append("query", reg.query);
    }

    // Add sort parameters if provided
    if (reg.sort) {
      params.append("sort", reg.sort);
    }

    if (reg.order) {
      params.append("order", reg.order);
    }

    return fetch
      .get(
        API_ROUTES.TRADE.MATCHED_TRADES_IN_LGS + `?${params.toString()}`,
        null
      )
      .then((res: LGSMatchedCardsResponse) => res);
  }
};
